import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { touchedBlocks } from "../blocks";
import { isCorrect, onSpellingChange } from "./checker";

export interface SpellState {
  decos: DecorationSet;
  /** The last change was typing: don't underline the word at the caret until it moves. */
  typing: boolean;
}

export const spellKey = new PluginKey<SpellState>("spellcheck");
const REFRESH = "refresh";

/** Letters and digits, with inner apostrophes ("don’t", "Bilbo’s"); hyphens split words. */
const WORD = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)*/gu;
const OPENERS = /[\s"“”'‘’([{*_—–-]/u;

function atSentenceStart(text: string, i: number): boolean {
  let j = i - 1;
  while (j >= 0 && OPENERS.test(text[j]!)) j--;
  return j < 0 || /[.!?…]/.test(text[j]!);
}

const isCapitalized = (w: string) => /^\p{Lu}/u.test(w);

/** Words that are never checked: numbers, acronyms, camelCase, single letters, URL and email parts. */
function exempt(text: string, start: number, word: string): boolean {
  if (word.length < 2 || /\p{N}/u.test(word)) return true;
  if (word === word.toUpperCase()) return true;
  if (/\p{Lu}/u.test(word.slice(1))) return true;
  const before = text[start - 1];
  const after = text.slice(start + word.length, start + word.length + 2);
  return (!!before && /[@/\\.:_#]/.test(before)) || /^(@|[./:][^\s])/.test(after);
}

/**
 * Capitalized words in mid-sentence are taken as names and not checked. A name that only
 * starts sentences is still accepted if it appears mid-sentence somewhere in the document.
 */
function collectNames(node: PMNode, names: Set<string>) {
  const text = node.textBetween(0, node.content.size, undefined, " ");
  for (const m of text.matchAll(WORD)) {
    if (isCapitalized(m[0]) && !atSentenceStart(text, m.index)) names.add(m[0]);
  }
}

function scanBlock(node: PMNode, pos: number, caret: number | null, names: Set<string>, out: Decoration[]) {
  const text = node.textBetween(0, node.content.size, undefined, " ");
  for (const m of text.matchAll(WORD)) {
    const word = m[0];
    if (exempt(text, m.index, word)) continue;
    if (isCapitalized(word)) {
      if (!atSentenceStart(text, m.index)) {
        names.add(word);
        continue;
      }
      if (names.has(word)) continue;
    }
    const from = pos + 1 + m.index;
    const to = from + word.length;
    if (caret !== null && caret >= from && caret <= to) continue;
    if (!isCorrect(word)) out.push(Decoration.inline(from, to, { class: "misspelled" }, { word }));
  }
}

function scanAll(doc: PMNode, caret: number | null, names: Set<string>): DecorationSet {
  doc.descendants((node) => {
    if (node.isTextblock) collectNames(node, names);
    return !node.isTextblock;
  });
  const out: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.isTextblock) scanBlock(node, pos, caret, names, out);
    return !node.isTextblock;
  });
  return DecorationSet.create(doc, out);
}

/** Quill's own spellchecker (replaces the browser's): wavy underlines on misspelled words. */
export const Spellcheck = Extension.create({
  name: "spellcheck",

  addProseMirrorPlugins() {
    const names = new Set<string>();
    return [
      new Plugin<SpellState>({
        key: spellKey,
        state: {
          init: (_, state) => ({ decos: scanAll(state.doc, null, names), typing: false }),
          apply(tr, prev, oldState) {
            const typing = tr.docChanged ? tr.selection.empty : tr.selectionSet ? false : prev.typing;
            const caret = typing ? tr.selection.head : null;
            if (tr.getMeta(spellKey) === REFRESH) return { decos: scanAll(tr.doc, caret, names), typing };
            if (!tr.docChanged && typing === prev.typing && !tr.selectionSet) return prev;

            const blocks = touchedBlocks(tr, oldState.selection.head);
            // Loading or pasting a lot: one full pass also learns names used later in the text.
            if (blocks.size > 50) return { decos: scanAll(tr.doc, caret, names), typing };
            let decos = prev.decos.map(tr.mapping, tr.doc);
            for (const pos of blocks) {
              const node = tr.doc.nodeAt(pos)!;
              const out: Decoration[] = [];
              scanBlock(node, pos, caret, names, out);
              decos = decos.remove(decos.find(pos, pos + node.nodeSize)).add(tr.doc, out);
            }
            return { decos, typing };
          },
        },
        props: {
          decorations(state) {
            return spellKey.getState(state)!.decos;
          },
          handleDOMEvents: {
            // Right-click on an underlined word: put the caret in it, which opens the spelling menu.
            contextmenu(view, event) {
              if (!(event.target as HTMLElement).closest?.(".misspelled")) return false;
              const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
              if (!at) return false;
              event.preventDefault();
              view.focus();
              view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, at.pos)));
              return true;
            },
          },
        },
        view(view) {
          let frame = 0;
          const off = onSpellingChange(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              if (!view.isDestroyed) view.dispatch(view.state.tr.setMeta(spellKey, REFRESH));
            });
          });
          return {
            destroy() {
              cancelAnimationFrame(frame);
              off();
            },
          };
        },
      }),
    ];
  },
});

/** The underlined word at the caret, if the spelling menu should offer fixes for it. */
export function misspellingAtCaret(state: Parameters<typeof spellKey.getState>[0]) {
  const spell = spellKey.getState(state);
  const { selection } = state;
  if (!spell || spell.typing || !selection.empty) return null;
  const [deco] = spell.decos.find(selection.head, selection.head);
  return deco ? { from: deco.from, to: deco.to, word: (deco.spec as { word: string }).word } : null;
}
